import { getAccessToken, logout } from './auth';

export interface CostEstimate {
  id:                     number;
  net_price:              number | null;
  bru_price:              number | null;
  vat:                    number | null;
  error_description:      string;
  repair_info:            string;
  insurance_info:         string | null;
  is_assona:              boolean;
  damage_picture_file_id: number | null;
  creator_name:           string;
  creation_timestamp:     string;
}

export interface KvTemplate {
  id:              number;
  key:             string;
  description:     string;
  text:            string;
  additional_text: string;
}

export interface CostEstimateCreatePayload {
  bru_price:               number;
  error_description:       string;
  repair_info?:            string;
  insurance_info?:         string;
  damage_picture_file_id?: number | null;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(
      body.reason ?? `Server-Fehler ${resp.status}`,
      resp.status,
      body.result,
      body.fields,
    );
  }

  return body;
}

/** Alle Kostenvoranschläge eines Tickets, neueste zuerst. */
export async function listCostEstimates(ticketId: number): Promise<CostEstimate[]> {
  const body = await request<CostEstimate>(`${BASE}/tickets/${ticketId}/cost-estimates`, { method: 'GET' });
  return body.items ?? [];
}

/** Legt einen Kostenvoranschlag an (nur im Status „Eingelangt" erlaubt). */
export async function createCostEstimate(ticketId: number, payload: CostEstimateCreatePayload): Promise<CostEstimate> {
  const body = await request<CostEstimate>(`${BASE}/tickets/${ticketId}/cost-estimates`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

/** Soft-Delete eines Kostenvoranschlags (setzt delete_timestamp serverseitig). */
export async function deleteCostEstimate(ticketId: number, kvId: number): Promise<void> {
  await request(`${BASE}/tickets/${ticketId}/cost-estimates/${kvId}`, { method: 'DELETE' });
}

/** Schadensarten-Vorlagen für das „Art der Beschädigung"-Dropdown. */
export async function listKvTemplates(): Promise<KvTemplate[]> {
  const body = await request<KvTemplate>(`${BASE}/kv-templates`, { method: 'GET' });
  return body.items ?? [];
}

/** Holt das KV-PDF MIT Bearer-Token (für Blob-Open im neuen Tab). */
export async function fetchCostEstimatePdfBlob(ticketId: number, kvId: number): Promise<Blob> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(`${BASE}/tickets/${ticketId}/cost-estimates/${kvId}/pdf`, {
      method:  'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }
  if (!resp.ok) {
    throw new ApiError(`PDF nicht abrufbar (${resp.status})`, resp.status, 'NOT_FOUND');
  }
  return await resp.blob();
}
